const CAP15 = {
  id: 15,
  slug: "cap15-fundamentos-blockchain",
  title: "Capítulo 15 — Fundamentos básicos de la tecnología Blockchain",
  sections: [
    {
      heading: "La estructura de cadena de bloques",
      text: [
        "La tecnología blockchain, también llamada 'cadena de bloques', está formada por varios elementos conectados que permiten su funcionamiento seguro y eficiente.",
        "Cada uno tiene un propósito: los nodos mantienen la red viva, los bloques almacenan información y los mecanismos de consenso validan esa información.",
        "Cuando Satoshi Nakamoto presentó Bitcoin en octubre de 2008, describió un sistema peer-to-peer (P2P) que permitía que personas pudieran enviar dinero electrónico sin intermediarios.",
        "Ese diseño se basó en la idea de blockchain: una red pública, descentralizada y segura, donde los participantes pueden intercambiar información o valor sin necesidad de confianza entre ellos."
      ]
    },

    {
      heading: "Componentes principales de la estructura blockchain",
      text: [
        "Para entender cómo funciona blockchain, primero es necesario conocer los elementos que forman su estructura básica.",
        "Estos son los componentes clave que hacen que una red blockchain funcione correctamente:"
      ],
      list: [
        "🔹 Capa de red o capa P2P (peer-to-peer).",
        "🔹 Nodos (participantes de la red).",
        "🔹 Bloques (unidades que almacenan transacciones).",
        "🔹 Transacciones (registros de intercambio de información o valor).",
        "🔹 Mecanismos de consenso (reglas para validar transacciones)."
      ]
    },

    {
      heading: "Capa de red o capa P2P",
      text: [
        "La capa P2P es la base de la comunicación en blockchain.",
        "Permite que los nodos se conecten entre sí, compartan información y mantengan sincronizados los datos de la red."
      ],
      list: [
        "🔹 Permite descubrir y conectar nodos automáticamente.",
        "🔹 Facilita la difusión de transacciones y bloques entre todos los participantes.",
        "🔹 Asegura que todos los nodos tengan la misma versión actualizada de la blockchain."
      ]
    },

    {
      heading: "¿Qué son los nodos?",
      text: [
        "Los nodos son los participantes de la red blockchain.",
        "Cada nodo puede ser un dispositivo (computadora, servidor, móvil) que almacena, transmite o valida información.",
        "En una blockchain, no todos los nodos hacen lo mismo: existe más de un tipo."
      ]
    },

    {
      heading: "Tipos de nodos en blockchain",
      text: [
        "En una red blockchain pueden existir diferentes tipos de nodos, cada uno con funciones específicas.",
        "Estos son los más importantes:"
      ]
    },

    {
      heading: "Nodos completos",
      text: [
        "Los nodos completos almacenan una copia completa de toda la blockchain y validan cada transacción que ocurre en la red.",
        "Ejecutan el software del protocolo y aplican las reglas de consenso para mantener la red segura y descentralizada."
      ],
      list: [
        "🔹 Almacenan el historial completo de la cadena de bloques.",
        "🔹 Validan transacciones y bloques.",
        "🔹 Difunden información a otros nodos.",
        "🔹 Requieren mayor capacidad de almacenamiento y responsabilidad."
      ]
    },

    {
      heading: "Nodos ligeros (SPV)",
      text: [
        "Los nodos ligeros no almacenan toda la blockchain. Solo descargan una parte y dependen de los nodos completos para verificar transacciones.",
        "Son ideales para dispositivos con recursos limitados, como teléfonos y tablets."
      ],
      list: [
        "🔹 Pueden verificar transacciones, pero no participan en su validación.",
        "🔹 No almacenan toda la cadena, solo cabeceras de bloques.",
        "🔹 Ofrecen rapidez y accesibilidad al usuario.",
        "🔹 Menos seguros que los nodos completos."
      ]
    },

    {
      heading: "Supernodos",
      text: [
        "Los supernodos son nodos completos que operan públicamente y permiten la interconexión y sincronización de otros nodos.",
        "Actúan como 'puentes' que mantienen la red unida y funcional."
      ],
      list: [
        "🔹 Están activos de forma continua.",
        "🔹 Facilitan la comunicación entre nodos.",
        "🔹 Distribuyen información rápidamente.",
        "🔹 Mejoran la resiliencia y descentralización de la red."
      ]
    },

    {
      heading: "Nodos mineros",
      text: [
        "Los nodos mineros participan en la creación de nuevos bloques y validan transacciones usando mecanismos como Proof of Work.",
        "Compiten resolviendo problemas matemáticos complejos y reciben recompensas por su labor."
      ],
      list: [
        "🔹 Validan transacciones y crean nuevos bloques.",
        "🔹 Utilizan hardware especializado para la minería (como ASICs).",
        "🔹 Reciben recompensas y comisiones de transacciones.",
        "🔹 Mantienen la seguridad y estabilidad de la blockchain."
      ]
    },

    {
      heading: "Conclusión",
      text: [
        "Los nodos y la capa P2P permiten que blockchain funcione sin una autoridad central.",
        "Cada tipo de nodo cumple una función específica, y juntos garantizan la seguridad, transparencia y descentralización de la red.",
        "En resumen, la fuerza de blockchain no está en un solo nodo, sino en la interacción coordinada de todos los participantes."
      ]
    }
  ]
};

export default CAP15;
